import get from 'lodash/get';
import map from 'lodash/map';
import sum from 'lodash/sum';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import concat from 'lodash/concat';
import round from 'lodash/round';
import values from 'lodash/values';
import forEach from 'lodash/forEach';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import split from 'lodash/split';
import filter from 'lodash/filter';
import groupBy from 'lodash/groupBy';
import countBy from 'lodash/countBy';
import startsWith from 'lodash/startsWith';
import slice from 'lodash/slice';

import {
    HoleData,
    HoleMapping,
    HoleDifficulty,
    HoleSummaryData,
    CourseData,
    CourseSummaryData,
    HoleDifficulties,
    NormalizedMatchData
} from '../../data_types/types';

import {
    HOLE_TO_SCENE,
    HOLE_PREFIX_TO_DATA,
    CourseData as CourseDataHoles
} from '../holes';


// these layouts are the same on both tracks so difficulties should be mapped
const sharedHoleDifficulties = {
    'MH_FPO2023_04': 'MH05',
    'MH_FPO2023_05': 'MH05',
    'MH_FPO2023_13': 'MH13',
    'MH_FPO2023_15': 'MH15',
    'CL_MPO2024_03': 'ECC03',
    'CL_MPO2024_10': 'CL_FPO2024_10',
    'CL_MPO2024_16': 'CL_FPO2024_16',
    'CL_FPO2024_13': 'CL_MPO2024_13',
};


const normalizeMapleHill = (_hole: string)=> {
    const [hole, version] = split(_hole, '_');
    const holeShorthand = hole.substring(0, 2);
    const holeNum: number = Number(hole.substring(2));

    // Gold is version "0" in difficulties mapping
    if (version === '0') {
        return {
            holeStr: `${holeShorthand}G${holeNum}`,
            holeShorthand: 'MHG',
            holeNum: holeNum
        };

    // Diamond is version "1" in difficulties mapping
    } else if (version === '1') {
        return {
            holeStr: `${holeShorthand}D${holeNum}`,
            holeShorthand: 'MHD',
            holeNum: holeNum
        };

    // Red is version "2" in difficulties mapping
    } else if (version === '2') {
        return {
            holeStr: `${holeShorthand}R${holeNum}`,
            holeShorthand: 'MHR',
            holeNum: holeNum
        };
    }

    // Gold 2024 is version "3" in difficulties mapping
    return {
        holeStr: `${holeShorthand}G_2024_${holeNum}`,
        holeShorthand: 'MHG2024',
        holeNum: holeNum
    };
};

const normalizeChampionsLanding = (_hole: string)=> {
    const [hole, version] = split(_hole, '_');
    const holeShorthand = hole.substring(0, 2);
    const holeNum: number = Number(hole.substring(2));

    if (version === '0') {
        return {
            holeStr: `${holeShorthand}${holeNum}`,
            holeShorthand: 'EC',
            holeNum: holeNum
        };

    } else if (version === '1') {
        return {
            holeStr: `CLMPO${holeNum}`,
            holeShorthand: 'CLMPO',
            holeNum: holeNum
        };
    }

    return {
        holeStr: `CLFPO${holeNum}`,
        holeShorthand: 'CLFPO',
        holeNum: holeNum
    };
};


export const normalizeHole = (_hole: string)=> {
    if (startsWith(_hole, 'MH')) {
        return normalizeMapleHill(_hole);

    } else if (startsWith(_hole, 'EC')) {
        return normalizeChampionsLanding(_hole);

    }

    return {
        holeStr: _hole,
        holeShorthand: _hole.substring(0, 2),
        holeNum: Number(_hole.substring(2))
    };

};

const DUMMY_DIFFICULTY: HoleDifficulty = {
    albatross: 0,
    average: -0.5,
    birdie: 0,
    bogey: 0,
    cappedAverage: 0,
    doubleBogey: 0,
    eagle: 0,
    eventCount: 0,
    par: 0,
    worse: 0,
    holeName: 'Unknown'
};

export const generateHoleData = (myPID: string, matchData: NormalizedMatchData[], holeDifficulties: HoleDifficulties): HoleData[]=> {
    const holeMapping: HoleMapping = {};

    forEach(matchData, (match)=> {
        const myPlayer = find(match.playerData, ['pID', myPID]);

        forEach(myPlayer!.scoreCard, (score, hole)=> {
            const { holeStr, holeShorthand, holeNum } = normalizeHole(hole);
            const holeData = get(HOLE_PREFIX_TO_DATA, holeShorthand);

            if (isNil(holeData)) {
                return;
            }

            if (!holeMapping.hasOwnProperty(holeStr)) {
                holeMapping[holeStr] = {
                    shorthand: holeStr,
                    fullName: getHoleFullName(holeShorthand, holeNum),
                    timesPlayed: 0,
                    scores: [],
                    difficulty: {},
                    par: 0,
                    elite: 0,
                    field_avg: 0
                };
            }

            const scene = get(HOLE_TO_SCENE, holeStr);
            const holePar = holeData.par[holeNum - 1];
            const holeElite = holeData.elite[holeNum - 1];

            var holeDifficulty = get(holeDifficulties, scene);

            // handle shared difficulties
            if (isNil(holeDifficulty)) {
                const sharedDiff = get(holeDifficulties, get(sharedHoleDifficulties, scene));

                if (sharedDiff) {
                    holeDifficulty = sharedDiff;
                } else {
                    holeDifficulty = DUMMY_DIFFICULTY;
                }
            }

            holeMapping[holeStr]['par'] = holePar;
            holeMapping[holeStr]['elite'] = holeElite;
            holeMapping[holeStr]['field_avg'] = round(holePar + holeDifficulty.average, 2);

            if (score > 0) {
                holeMapping[holeStr] = {
                    ...holeMapping[holeStr],
                    timesPlayed: holeMapping[holeStr].timesPlayed + 1,
                    scores: concat(holeMapping[holeStr].scores, score),
                    difficulty: holeDifficulty
                };
            }
        });
    });

    return filter(values(holeMapping), (hole)=> hole.timesPlayed !== 0);
};


export const generateHoleSummaryData = (records: HoleData[]): HoleSummaryData=> {
    const numRecords = 6;

    return {
        best_holes: getBestHoles(records, numRecords),
        worst_holes: getWorstHoles(records, numRecords),
        overall_vs_field: getTotalStrokesGainedOnField(records),
        count_better_than_par: getCountBetterThanPar(records),
        count_worse_than_par: getCountWorseThanPar(records),
        count_better_than_avg: getCountBetterThanAvg(records),
        count_worse_than_avg: getCountWorseThanAvg(records),
        count_better_than_elite: getCountBetterThanElite(records),
        count_worse_than_elite: getCountWorseThanElite(records),
    };
};


export const generateCourseSummaryData = (records: HoleData[]): CourseSummaryData=> {
    const groupedHoles = groupBy(records, (hole)=> {
        if (startsWith(hole.shorthand, 'MH')) {
            return hole.shorthand.substring(0, 3);

        } else if (startsWith(hole.shorthand, 'CL')) {
            return hole.shorthand.substring(0, 5);

        }

        return hole.shorthand.substring(0, 2);
    });

    const courseSummaryData: CourseSummaryData = {};

    forEach(groupedHoles, (holes, courseShortname)=> {
        const courseData = get(HOLE_PREFIX_TO_DATA, courseShortname);

        const par = sum(courseData.par);
        const elite = sum(courseData.elite);

        const myHoleAvgs = map(holes, getMyHoleAverage);
        const myCourseAvg = round(sum(myHoleAvgs), 2);

        const fieldHoleAvgs = map(holes, (hole)=> hole.field_avg);
        const fieldCourseAvg = round(sum(fieldHoleAvgs), 2);

        const data: CourseData = {
            name: courseData.name,
            shortname: courseShortname,
            ctv_order: courseData.ctv_order,
            par: par,
            elite: elite,
            elite_score: round(elite - par, 2),
            field_avg: fieldCourseAvg,
            field_score: round(fieldCourseAvg - par, 2),
            my_avg: myCourseAvg,
            my_score: round(myCourseAvg - par, 2),
            me_vs_elite: round(myCourseAvg - elite, 2),
            me_vs_field: round(myCourseAvg - fieldCourseAvg, 2),
        };

        if (holes.length === courseData.par.length) {
            courseSummaryData[courseShortname] = data;
        }
    });

    return courseSummaryData;
};


export const getHoleFullName = (holePrefix: string, holeNum: number): string=> {
    const courseData = get(HOLE_PREFIX_TO_DATA, holePrefix);

    if (courseData) {
        return `${courseData.name} ${holeNum}`;
    }

    return holePrefix;
};


export const getMyHoleAverage = (hole: HoleData): number=>
    round(sum(hole.scores) / hole.timesPlayed, 2) || 0;


export const getStrokesGainedOnField = (hole: HoleData): number=>
    round(hole.field_avg - getMyHoleAverage(hole), 2);


export const getStrokesGainedOnElite = (hole: HoleData): number=>
    round(hole.elite - getMyHoleAverage(hole), 2);


export const getTotalStrokesGainedOnField = (holes: HoleData[]): number=>
    round(sum(map(holes, h=>h.field_avg)) - sum(map(holes, getMyHoleAverage)), 2) || 0;


export const getTotalAces = (hole: HoleData): number=>
    get(countBy(hole.scores, score=> score), '1', 0);


export const getBestHoles = (holes: HoleData[], numRecords: number): HoleData[]=>
    slice(reverse(sortBy(holes, getStrokesGainedOnField)), 0, numRecords);


export const getWorstHoles = (holes: HoleData[], numRecords: number): HoleData[]=>
    slice(sortBy(holes, getStrokesGainedOnField), 0, numRecords);


export const getCountComparisonGenric = (holes: HoleData[], cmpFunc: (hole: HoleData)=> boolean): number=> {
    var cnt = 0;

    forEach(holes, (hole)=> {
        if (cmpFunc(hole)) {
            cnt = cnt + 1;
        }
    });

    return cnt;
};

export const getCountBetterThanPar = (holes: HoleData[]): number=>
    getCountComparisonGenric(holes, (hole)=> getMyHoleAverage(hole) <= hole.par);

export const getCountWorseThanPar = (holes: HoleData[]): number=>
    getCountComparisonGenric(holes, (hole)=> getMyHoleAverage(hole) > hole.par);

export const getCountBetterThanAvg = (holes: HoleData[]): number=>
    getCountComparisonGenric(holes, (hole)=> getMyHoleAverage(hole) <= hole.field_avg);

export const getCountWorseThanAvg = (holes: HoleData[]): number=>
    getCountComparisonGenric(holes, (hole)=> getMyHoleAverage(hole) > hole.field_avg);

export const getCountBetterThanElite = (holes: HoleData[]): number=>
    getCountComparisonGenric(holes, (hole)=> getMyHoleAverage(hole) < hole.elite);

export const getCountWorseThanElite = (holes: HoleData[]): number=>
    getCountComparisonGenric(holes, (hole)=> getMyHoleAverage(hole) >= hole.elite);

